import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Sparkles, Loader2, Printer, Users, RotateCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { API_BASE } from '@/lib/api';

const API = API_BASE;

/**
 * Full-screen-ish photo viewer with the AI-generated caption + tag chips
 * lazy-loaded below the image. Free families see a "Premium" upsell instead
 * of tags.
 */
export default function PhotoModal({ fileId, open, onOpenChange, onPrint }) {
  const [meta, setMeta] = useState(null);     // { ai_tags, is_premium_feature }
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    if (!open || !fileId) {
      setMeta(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/photos/${fileId}/ai-tags`, { withCredentials: true })
      .then(({ data }) => {
        if (!cancelled) setMeta(data);
      })
      .catch(() => { if (!cancelled) setMeta(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [fileId, open]);

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const { data } = await axios.post(
        `${API}/photos/${fileId}/ai-tags/regenerate`,
        {},
        { withCredentials: true },
      );
      setMeta((m) => ({ ...(m || {}), ai_tags: data.ai_tags, is_premium_feature: true }));
      toast.success('Tags refreshed!');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not regenerate tags';
      toast.error(detail);
    } finally {
      setRegenerating(false);
    }
  };

  const src = fileId ? `${API}/files/${fileId}` : '';
  const tags = meta?.ai_tags;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl rounded-3xl p-0 overflow-hidden" data-testid="photo-modal">
        {fileId && (
          <>
            <DialogTitle className="sr-only">Family photo</DialogTitle>
            <DialogDescription className="sr-only">
              {tags?.caption || 'A photo from your family'}
            </DialogDescription>
            <div className="bg-black flex items-center justify-center max-h-[70vh]">
              <img
                src={src}
                alt={tags?.caption || 'Family photo'}
                className="max-h-[70vh] w-auto object-contain"
                data-testid="photo-modal-img"
              />
            </div>
            <div className="p-5 space-y-3">
              {loading && (
                <p className="text-sm text-stone-500 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Reading photo…
                </p>
              )}

              {!loading && tags && (
                <>
                  {tags.caption && (
                    <p className="text-stone-800 leading-relaxed">{tags.caption}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    {(tags.themes || []).map((t) => (
                      <span
                        key={t}
                        className="px-2.5 py-1 rounded-full bg-gradient-to-br from-teal-100 to-cyan-100 text-teal-800 font-semibold"
                        data-testid={`tag-theme-${t}`}
                      >
                        {t}
                      </span>
                    ))}
                    {tags.scene && (
                      <span className="px-2.5 py-1 rounded-full bg-stone-100 text-stone-700">
                        {tags.scene}
                      </span>
                    )}
                    {tags.people_count > 0 && (
                      <span className="px-2.5 py-1 rounded-full bg-stone-100 text-stone-700 inline-flex items-center gap-1">
                        <Users className="w-3 h-3" /> {tags.people_count}
                      </span>
                    )}
                    {(tags.tags || []).map((t) => (
                      <span
                        key={t}
                        className="px-2.5 py-1 rounded-full bg-stone-100 text-stone-600"
                        data-testid={`tag-${t}`}
                      >
                        #{t}
                      </span>
                    ))}
                  </div>
                </>
              )}

              {!loading && !tags && meta?.is_premium_feature && (
                <div className="rounded-2xl border border-teal-100 bg-teal-50 p-4 flex items-start gap-3" data-testid="ai-tags-premium-upsell">
                  <Sparkles className="w-5 h-5 text-teal-600 shrink-0 mt-0.5" />
                  <div className="text-sm text-stone-700">
                    <p className="font-medium text-stone-900">AI tags &amp; smart collections</p>
                    <p>Upgrade to Family or Premium to auto-tag every photo and unlock themed collections.</p>
                  </div>
                </div>
              )}

              {!loading && !tags && meta && !meta.is_premium_feature && (
                <p className="text-sm text-stone-500">
                  This photo hasn&apos;t been tagged yet. Tags appear automatically a few seconds after upload.
                </p>
              )}

              <div className="flex flex-wrap gap-2 pt-2">
                {onPrint && (
                  <Button
                    onClick={() => onPrint(fileId)}
                    variant="outline"
                    className="rounded-full"
                    data-testid="photo-modal-print"
                  >
                    <Printer className="w-4 h-4 mr-2" /> Print this photo
                  </Button>
                )}
                {tags && (
                  <Button
                    onClick={regenerate}
                    disabled={regenerating}
                    variant="ghost"
                    className="rounded-full text-stone-600"
                    data-testid="photo-modal-regen"
                  >
                    {regenerating ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Re-tagging…</>
                    ) : (
                      <><RotateCw className="w-4 h-4 mr-2" /> Re-tag with AI</>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
